import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';

import { Card } from 'react-bootstrap';
import PageTitle from '../../../layouts/PageTitle';
import { useStaticCollections } from '../../../../context/StaticCollections';
import { useAdminCollections } from '../../../../context/AdminCollections';
import { useLoggedInUserContextProvider } from '../../../../context/LoggedInUserContext';
import { addCmseCommitteeToSectionalCommitteeArray } from '../../../../utils/addCmseCommitteeToSectionalCommitteeArray';

import { useCOIData } from '../../../hooks/useCOIData';
import { useUserPermissions } from '../../../hooks/useUserPermissions';
import { useCommitteeData } from '../../../hooks/useCommitteeData';
import { useAllCommitteesData } from '../../../hooks/useAllCommitteesData';
import { useCouncilMemberFiltering } from '../../../hooks/useCouncilMemberFiltering';

import COIReportFilters from '../../Reports/COIReportFilters';
import COITable from '../../Table/COITable';
import ExportButton, { ExportAllButton } from '../../Table/ExportButton';
import CommitteeBanner from '../../CommitteeBanner';

import FilteringTable from '../../Table/FilteringTable/FilteringTable';
import ColumnFilter from '../../Table/FilteringTable/ColumnFilter';

import { tableColumnNamesCOI } from '../../../../utils/tableColumnNamesCOI';
import { tableRowsValuesCOI } from '../../../../utils/tableRowsValuesCOI';
import { filteringTableGetTableColumns } from '../../../../utils/filteringTableGetTableColumns';
import { CM_PRIMARY_ID, SE_COMMITTEE_ID } from '../../../../utils/constants';

const buildExportAction = ({
  candidates,
  committeeMembers,
  getCOIForCandidateAndMember,
  committeeName,
}) => {
  if (candidates.length === 0) return null;
  return (
    <ExportButton
      candidates={candidates}
      committeeMembers={committeeMembers}
      getCOIForCandidateAndMember={getCOIForCandidateAndMember}
      committeeName={committeeName}
    />
  );
};

const CommitteeContent = ({
  candidates,
  committeeMembers,
  getCOIForCandidateAndMember,
}) => (
  <div className="table-responsive">
    <COITable
      candidates={candidates}
      committeeMembers={committeeMembers}
      getCOIForCandidateAndMember={getCOIForCandidateAndMember}
    />
  </div>
);

CommitteeContent.propTypes = {
  candidates: PropTypes.array.isRequired,
  committeeMembers: PropTypes.array.isRequired,
  getCOIForCandidateAndMember: PropTypes.func.isRequired,
};

const ViewToggleButtons = ({ useFilteringTable, setUseFilteringTable }) => (
  <div className="d-flex align-items-center justify-content-end mt-xl-5 mb-3">
    <span className="me-2 text-muted small">View:</span>
    <div className="btn-group btn-group-sm" role="group">
      <button
        type="button"
        className={`btn ${!useFilteringTable ? 'btn-primary' : 'btn-outline-primary'}`}
        onClick={() => setUseFilteringTable(false)}
      >
        Standard
      </button>
      <button
        type="button"
        className={`btn ${useFilteringTable ? 'btn-primary' : 'btn-outline-primary'}`}
        onClick={() => setUseFilteringTable(true)}
      >
        Filtering
      </button>
    </div>
  </div>
);

ViewToggleButtons.propTypes = {
  useFilteringTable: PropTypes.bool.isRequired,
  setUseFilteringTable: PropTypes.func.isRequired,
};

const COIReport = () => {
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationTitle, setNotificationTitle] = useState('');
  const [selectedCommittee, setSelectedCommittee] = useState(null);
  const [useFilteringTable, setUseFilteringTable] = useState(false);
  const [showCouncilMembersOnly, setShowCouncilMembersOnly] = useState(false);

  const {
    sectionalCommitteesArray,
    fellowsArray,
    nonAasRefereesArray,
    isLoading: staticLoading,
  } = useStaticCollections();

  const {
    candidateArray,
    committeeArray,
    coiDeclarationArray,
    nominationArray,
    councilMembersArray,
    nominationStateArray,
    isLoading: adminLoading,
  } = useAdminCollections();

  const { userDetails } = useLoggedInUserContextProvider();

  const {
    isAdmin,
    isSCSecretary,
    hasAccess,
    userCommittee,
    userCommittees,
    isSCChair,
    isSCObserver,
    isCouncilMember,
  } = useUserPermissions(userDetails, committeeArray);

  const sectionalCommitteesWithCmse = useMemo(
    () =>
      addCmseCommitteeToSectionalCommitteeArray(sectionalCommitteesArray, true),
    [sectionalCommitteesArray],
  );

  const availableCommittees = useMemo(() => {
    if (isAdmin || isSCSecretary || isCouncilMember) {
      return sectionalCommitteesWithCmse;
    }

    if (isSCObserver && userCommittees.length > 0) {
      const observerCommitteeIds = userCommittees.map((c) => c.id);

      if (observerCommitteeIds.includes(CM_PRIMARY_ID)) {
        return sectionalCommitteesWithCmse.filter(
          (committee) =>
            committee.id === CM_PRIMARY_ID || committee.id === SE_COMMITTEE_ID,
        );
      }

      return sectionalCommitteesWithCmse.filter((committee) =>
        observerCommitteeIds.includes(committee.id),
      );
    }

    if (userCommittee?.id === 14) {
      return sectionalCommitteesWithCmse.filter(
        (committee) =>
          committee.id === userCommittee.id || committee.id === SE_COMMITTEE_ID,
      );
    }

    if (userCommittee) {
      return sectionalCommitteesWithCmse.filter(
        (committee) => committee.id === userCommittee.id,
      );
    }

    return [];
  }, [
    isAdmin,
    isSCSecretary,
    isSCObserver,
    isCouncilMember,
    userCommittee,
    userCommittees,
    sectionalCommitteesWithCmse,
  ]);

  const { coiData, loading, coiLookup } = useCOIData(
    selectedCommittee,
    coiDeclarationArray,
    availableCommittees,
    nominationArray,
    committeeArray,
  );

  const { coiData: allCoiData, coiLookup: allCoiLookup } = useCOIData(
    null,
    coiDeclarationArray,
    availableCommittees,
    nominationArray,
    committeeArray,
  );

  const committeeData = useMemo(() => {
    if (!selectedCommittee || !committeeArray.length) return null;

    if (selectedCommittee === 15) {
      return committeeArray.find((c) => c.id === CM_PRIMARY_ID);
    }

    return committeeArray.find((c) => c.id === parseInt(selectedCommittee));
  }, [selectedCommittee, committeeArray]);

  const singleCommitteeData = useCommitteeData(
    selectedCommittee ? coiData : [],
    candidateArray,
    committeeData,
    fellowsArray,
    nonAasRefereesArray,
  );

  const allCommitteesDataRaw = useAllCommitteesData(
    selectedCommittee === null ? coiData : allCoiData,
    candidateArray,
    committeeArray,
    sectionalCommitteesWithCmse,
    fellowsArray,
    nonAasRefereesArray,
  );

  const memoizedSingleCommitteeData = useMemo(
    () => singleCommitteeData,
    [JSON.stringify(singleCommitteeData)],
  );

  const memoizedAllCommitteesData = useMemo(
    () => allCommitteesDataRaw,
    [JSON.stringify(allCommitteesDataRaw)],
  );

  const candidates = useMemo(
    () =>
      selectedCommittee
        ? memoizedSingleCommitteeData.candidates
        : memoizedAllCommitteesData.candidates,
    [selectedCommittee, memoizedSingleCommitteeData, memoizedAllCommitteesData],
  );

  const committeeMembers = useMemo(
    () =>
      selectedCommittee
        ? memoizedSingleCommitteeData.committeeMembers
        : memoizedAllCommitteesData.committeeMembers,
    [selectedCommittee, memoizedSingleCommitteeData, memoizedAllCommitteesData],
  );

  const committees = useMemo(
    () => memoizedAllCommitteesData.committees || [],
    [memoizedAllCommitteesData],
  );

  const { filteredCandidates, filteredCommitteeMembers, filteredCommittees } =
    useCouncilMemberFiltering({
      showCouncilMembersOnly,
      councilMembersArray,
      coiDeclarationArray,
      nominationStateArray,
      nominationArray,
      candidates,
      committeeMembers,
      committees,
    });

  const [coiTableData, setCoiTableData] = useState([]);
  const [coiTableColumns, setCoiTableColumns] = useState([]);

  const getCOIForCandidateAndMember = useCallback(
    (candidateId, userId) => {
      const key = `${candidateId}-${userId}`;
      const declaration = selectedCommittee
        ? coiLookup[key]
        : allCoiLookup[key];

      if (!declaration) return { types: '', detail: '' };

      const coiTypes =
        declaration.attributes.coiTypes?.data
          ?.map((type) => type.attributes.name)
          .join('<br />') || '';

      const coiCategory =
        declaration.attributes.coiTypes?.data?.[0]?.attributes?.category || '';

      const coiDetail = declaration.attributes.coiDetail || '';
      const declarationDate = declaration.attributes.updatedAt;

      return {
        types: coiTypes,
        detail: coiDetail,
        category: coiCategory,
        declarationDate,
      };
    },
    [coiLookup, allCoiLookup, selectedCommittee],
  );

  const getCommitteeName = useCallback(() => {
    if (!selectedCommittee) return 'All Committees';

    const committee = sectionalCommitteesWithCmse.find(
      (item) => item.id === parseInt(selectedCommittee),
    );

    return committee?.attributes?.name || '';
  }, [selectedCommittee, sectionalCommitteesWithCmse]);

  const getCommitteeCandidateCount = useCallback(
    (committeeId) => {
      const displayCommittees = showCouncilMembersOnly
        ? filteredCommittees
        : committees;
      const committee = displayCommittees.find((c) => c.id === committeeId);
      return committee ? committee.candidates.length : 0;
    },
    [showCouncilMembersOnly, filteredCommittees, committees],
  );

  const displayCommitteesForExport = useMemo(
    () => (showCouncilMembersOnly ? filteredCommittees : committees),
    [showCouncilMembersOnly, filteredCommittees, committees],
  );

  useEffect(() => {
    if (!hasAccess) {
      setNotificationMessage('You do not have permission to view COI reports.');
      setNotificationTitle('Access Denied');
    }
  }, [hasAccess]);

  useEffect(() => {
    if (
      !isAdmin &&
      !isCouncilMember &&
      !isSCObserver &&
      userCommittee &&
      !selectedCommittee
    ) {
      if (userCommittee?.id === 14 && isSCChair) {
        setSelectedCommittee(null);
      } else {
        setSelectedCommittee(userCommittee.id);
      }
    }
  }, [
    isAdmin,
    isSCChair,
    isSCObserver,
    isCouncilMember,
    userCommittee,
    selectedCommittee,
  ]);

  useEffect(() => {
    const displayMembers = showCouncilMembersOnly
      ? filteredCommitteeMembers
      : committeeMembers;
    const displayCommittees = showCouncilMembersOnly
      ? filteredCommittees
      : committees;
    const displayCandidates = showCouncilMembersOnly
      ? filteredCandidates
      : candidates;

    if (displayCandidates.length > 0 && displayMembers.length > 0) {
      const tableColumns = filteringTableGetTableColumns(
        tableColumnNamesCOI,
        ColumnFilter,
      );

      let tableData;
      if (selectedCommittee) {
        tableData = tableRowsValuesCOI(
          displayCandidates,
          displayMembers,
          getCOIForCandidateAndMember,
          getCommitteeName(),
        );
      } else {
        const allTableData = [];
        displayCommittees.forEach((committee) => {
          const committeeRows = tableRowsValuesCOI(
            committee.candidates,
            committee.committeeMembers,
            getCOIForCandidateAndMember,
            committee.name,
          );
          allTableData.push(...committeeRows);
        });
        tableData = allTableData;
      }

      setCoiTableColumns(tableColumns);
      setCoiTableData(tableData);
    } else {
      setCoiTableData([]);
    }
  }, [
    candidates,
    committeeMembers,
    committees,
    selectedCommittee,
    showCouncilMembersOnly,
    filteredCommitteeMembers,
    filteredCommittees,
    filteredCandidates,
    getCOIForCandidateAndMember,
    getCommitteeName,
  ]);

  const handleCommitteeChange = (committeeId) => {
    setSelectedCommittee(committeeId);
    setNotificationMessage('');
    setNotificationTitle('');
  };

  const renderReportContent = () => {
    if (loading) {
      return <div className="text-center">Loading COI report...</div>;
    }

    const displayMembers = showCouncilMembersOnly
      ? filteredCommitteeMembers
      : committeeMembers;
    const displayCommittees = showCouncilMembersOnly
      ? filteredCommittees
      : committees;
    const displayCandidates = showCouncilMembersOnly
      ? filteredCandidates
      : candidates;

    if (showCouncilMembersOnly && selectedCommittee !== null) {
      const hasCouncilMembers = councilMembersArray?.length > 0;

      if (!hasCouncilMembers) {
        return (
          <div className="alert alert-info text-center">
            No council members found for this committee.
          </div>
        );
      }

      if (displayCandidates.length === 0) {
        return (
          <div className="alert alert-info text-center">
            No candidates have been recommended to council for this committee.
          </div>
        );
      }
    }

    if (showCouncilMembersOnly && selectedCommittee === null) {
      const hasCouncilMembers = councilMembersArray?.length > 0;

      if (!hasCouncilMembers) {
        return (
          <div className="alert alert-info text-center">
            No council members found across all committees.
          </div>
        );
      }

      if (displayCommittees.length === 0) {
        return (
          <div className="alert alert-info text-center">
            No candidates have been recommended to council across all
            committees.
          </div>
        );
      }
    }

    if (useFilteringTable) {
      return (
        <div className="mt-xl-5">
          <FilteringTable
            componentName="COIReport"
            columns={coiTableColumns}
            data={coiTableData}
            urlString=""
            buttonText=""
            isLoading={staticLoading || adminLoading || loading}
          />
        </div>
      );
    }

    if (selectedCommittee === null) {
      const sortedCommittees = displayCommittees.sort((a, b) => a.id - b.id);

      return (
        <>
          {sortedCommittees.map((committee) => (
            <div key={committee.id} className="committee-section mt-xl-5">
              <CommitteeBanner
                name={committee.name}
                actions={buildExportAction({
                  candidates: committee.candidates,
                  committeeMembers: committee.committeeMembers,
                  getCOIForCandidateAndMember,
                  committeeName: committee.name,
                })}
              />
              <Card className="card-two">
                <Card.Body>
                  <CommitteeContent
                    candidates={committee.candidates}
                    committeeMembers={committee.committeeMembers}
                    getCOIForCandidateAndMember={getCOIForCandidateAndMember}
                  />
                </Card.Body>
              </Card>
            </div>
          ))}
        </>
      );
    }

    return (
      <CommitteeContent
        candidates={displayCandidates}
        committeeMembers={displayMembers}
        getCOIForCandidateAndMember={getCOIForCandidateAndMember}
      />
    );
  };

  if (!hasAccess) {
    return (
      <PageTitle
        motherMenu="Nominations"
        activeMenu="COI Report"
        notificationMessage={notificationMessage}
        notificationTitle={notificationTitle}
      />
    );
  }

  return (
    <>
      <PageTitle
        motherMenu="Nominations"
        activeMenu="COI Report"
        notificationMessage={notificationMessage}
        notificationTitle={notificationTitle}
      />

      {(isAdmin || isCouncilMember || isSCSecretary) && !useFilteringTable && (
        <ExportAllButton
          committees={displayCommitteesForExport}
          getCOIForCandidateAndMember={getCOIForCandidateAndMember}
          showCouncilMembersOnly={showCouncilMembersOnly}
        />
      )}

      <COIReportFilters
        isAdmin={isAdmin}
        isSCSecretary={isSCSecretary}
        isSCObserver={isSCObserver}
        isCouncilMember={isCouncilMember}
        availableCommittees={availableCommittees}
        selectedCommittee={selectedCommittee}
        sectionalCommitteesWithCmse={sectionalCommitteesWithCmse}
        onCommitteeChange={handleCommitteeChange}
        getCommitteeCandidateCount={getCommitteeCandidateCount}
        showCouncilMembersOnly={showCouncilMembersOnly}
        onMemberTypeChange={setShowCouncilMembersOnly}
      />

      {(selectedCommittee || selectedCommittee === null) && (
        <>
          <ViewToggleButtons
            useFilteringTable={useFilteringTable}
            setUseFilteringTable={setUseFilteringTable}
          />

          {useFilteringTable || selectedCommittee === null ? (
            renderReportContent()
          ) : (
            <div className="committee-section">
              <CommitteeBanner
                name={getCommitteeName()}
                actions={buildExportAction({
                  candidates: showCouncilMembersOnly
                    ? filteredCandidates
                    : candidates,
                  committeeMembers: showCouncilMembersOnly
                    ? filteredCommitteeMembers
                    : committeeMembers,
                  getCOIForCandidateAndMember,
                  committeeName: getCommitteeName(),
                })}
              />
              <Card className="card-two">
                <Card.Body>{renderReportContent()}</Card.Body>
              </Card>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default COIReport;
